import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { SideNav } from "../components/SideNav";
import { EuropeAboutCarousel } from "../components/EuropeAboutCarousel";
import { InstallApp } from "../components/InstallApp";
import { WeatherBanner } from "../components/WeatherBanner";
import { ActusVille } from "../components/ActusVille";
import { useCountry } from "../context/CountryContext";
import api, { fcfa, API } from "../lib/api";
import { useZone } from "../context/ZoneContext";
import { useAuth } from "../context/AuthContext";
import { useBrand } from "../lib/brand";
import {
  MapPin, Wallet, Star, ShieldCheck, CreditCard, Route, Bike, ArrowRight, Check, Package, Car, Clock, Download, Target, Users, CarTaxiFront, FileText, Phone,
} from "lucide-react";

const HERO = "https://customer-assets-agu9un31.emergentagent.net/job_moto-africa/artifacts/y975lr6z_image.jpeg";
const HERO_IMAGES = ["https://images.pexels.com/photos/39128403/pexels-photo-39128403.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260", "https://images.pexels.com/photos/33757406/pexels-photo-33757406.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260", "https://images.pexels.com/photos/33757408/pexels-photo-33757408.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260"];
const HERO_IMAGES_EU = ["/hero-nold-car.jpg", "/hero-nold-street.jpg", "/hero-nold-bike.jpg"];
const TRAFFIC = "https://images.unsplash.com/photo-1702725542848-ab2f0b29ffae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxhZnJpY2FuJTIwY2l0eSUyMHN0cmVldCUyMHRyYWZmaWN8ZW58MHx8fHwxNzg1MDkzNTU2fDA&ixlib=rb-4.1.0&q=85";
const MOTO_IMG = "https://images.pexels.com/photos/33757406/pexels-photo-33757406.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CAR_IMG = "https://images.pexels.com/photos/4606336/pexels-photo-4606336.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const DELIVERY_IMG = "https://images.pexels.com/photos/6995138/pexels-photo-6995138.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const VELO_IMG = "https://images.unsplash.com/photo-1611068562065-994ba66501ba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxiaWN5Y2xlJTIwY291cmllciUyMGRlbGl2ZXJ5JTIwY2l0eXxlbnwwfHx8fDE3ODUxNTA0OTV8MA&ixlib=rb-4.1.0&q=85";
const CITY_IMG = "https://images.pexels.com/photos/17290973/pexels-photo-17290973.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";
const DRIVERS_IMG = "https://images.pexels.com/photos/39128403/pexels-photo-39128403.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const RIDER_WOMAN = "https://images.pexels.com/photos/6280630/pexels-photo-6280630.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

// Direct-download signed Android APK (generated via Bubblewrap TWA).
const APK_URL = "/bentskin-go.apk";

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, delay: d, ease: [0.22, 1, 0.36, 1] },
});

export default function Landing() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { countries, currentCountry } = useCountry();
  const [promo, setPromo] = useState(null);
  const { zone: zoneRaw, detectCc, zoneView } = useZone();
  const { user } = useAuth();
  const isAdmin = !!(user && (user.role === "admin" || user.is_super_admin));
  const brand = useBrand();
  const zone = zoneRaw || "africa";
  const heroImages = zone === "europe" ? HERO_IMAGES_EU : HERO_IMAGES;
  const [heroIdx, setHeroIdx] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setHeroIdx((i) => (i + 1) % heroImages.length), 4500);
    return () => clearInterval(iv);
  }, [heroImages.length]);
  const HERO_PLACE = { CM: "au Cameroun", BE: "en Belgique", FR: "en France", NL: "aux Pays-Bas", LU: "au Luxembourg", GA: "au Gabon", TD: "au Tchad", CI: "en Côte d'Ivoire", SN: "au Sénégal" };
  const heroPlace = zoneView ? (zone === "europe" ? "en Belgique" : "au Cameroun") : (HERO_PLACE[detectCc] || (zone === "europe" ? "en Europe" : "au Cameroun"));
  const [now, setNow] = useState(Date.now());
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [complaint, setComplaint] = useState({ name: "", phone: "", category: "litige", ride_ref: "", message: "" });
  const [complaintSent, setComplaintSent] = useState(false);
  const submitComplaint = async (e) => {
    e.preventDefault();
    if (complaint.message.trim().length < 5) return;
    try { await api.post("/complaints", complaint); setComplaintSent(true); setComplaint({ name: "", phone: "", category: "litige", ride_ref: "", message: "" }); } catch (err) { /* noop */ }
  };
  const [agentApp, setAgentApp] = useState({ name: "", email: "", phone: "", zone: "", message: "" });
  const [agentAppSent, setAgentAppSent] = useState(false);
  const submitAgentApp = async (e) => {
    e.preventDefault();
    if (!agentApp.name.trim() || !agentApp.email.trim()) return;
    try { await api.post("/agent-applications", agentApp); setAgentAppSent(true); setAgentApp({ name: "", email: "", phone: "", zone: "", message: "" }); } catch (err) { /* noop */ }
  };

  useEffect(() => {
    api.get("/promo").then((r) => setPromo(r.data)).catch(() => {});
    const iv = setInterval(() => setNow(Date.now()), 1000);
    const onPrompt = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => { clearInterval(iv); window.removeEventListener("beforeinstallprompt", onPrompt); };
  }, []);

  const androidInstall = async () => {
    if (APK_URL) { window.location.href = APK_URL; return; }
    if (deferredPrompt) { deferredPrompt.prompt(); await deferredPrompt.userChoice; setDeferredPrompt(null); return; }
    const s = document.getElementById("install");
    if (s) s.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const promoDate = promo?.free_until
    ? new Date(promo.free_until).toLocaleDateString(i18n.language === "en" ? "en-GB" : "fr-FR", { day: "numeric", month: "long", year: "numeric" })
    : "";

  const countdown = (() => {
    if (!promo?.free_until) return null;
    let diff = Math.max(0, new Date(promo.free_until).getTime() - now);
    const d = Math.floor(diff / 86400000); diff -= d * 86400000;
    const h = Math.floor(diff / 3600000); diff -= h * 3600000;
    const m = Math.floor(diff / 60000); diff -= m * 60000;
    const s = Math.floor(diff / 1000);
    return { d, h, m, s };
  })();

  const [visits, setVisits] = useState(null);
  useEffect(() => {
    const once = sessionStorage.getItem("bsg_visited");
    const req = once ? api.get("/visits") : api.post("/visits");
    if (!once) sessionStorage.setItem("bsg_visited", "1");
    req.then((r) => setVisits(r.data?.count ?? null)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#FDF8F0]">
      <SideNav />
      <div className="lg:pl-72">

      <WeatherBanner />

      {/* ACCUEIL ÉPURÉ — question centrale + 3 choix de service illustrés */}
      <section data-testid="home-service-selector" className="relative overflow-hidden border-b border-[#F0E7DC] bg-gradient-to-br from-[#FFF6EC] via-white to-[#EAF9F1]">
        <div className="pointer-events-none absolute -top-24 -left-20 w-72 h-72 rounded-full bg-[#FF8800]/25 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -right-10 w-80 h-80 rounded-full bg-[#00A651]/20 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 pt-10 pb-12 sm:pt-14 sm:pb-16">
          <div className="text-center max-w-2xl mx-auto">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FFF3EC] text-[#E05D00] text-xs font-bold uppercase tracking-widest">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF6B00]" /> Mboa Go
            </span>
            <h1 className="mt-4 font-head font-black text-[#0B1320] text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
              Que voulez-vous <span className="text-[#FF6B00]">faire</span>&nbsp;?
            </h1>
            <p className="mt-3 text-[#334155] text-sm sm:text-base">Choisissez un service — pas d'inscription pour commencer.</p>
          </div>

          <div className="mt-8 sm:mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
            {[
              { id: "moto", intent: "moto", emoji: "🛺", label: "Taxi-moto", desc: "Rapide en ville", img: MOTO_IMG },
              { id: "car", intent: "car", emoji: "🚗", label: "Voiture taxi", desc: "Confort & bagages", img: CAR_IMG },
              { id: "delivery", intent: "delivery", emoji: "📦", label: "Livraison", desc: "Colis & courses", img: DELIVERY_IMG },
            ].map((c) => (
              <button key={c.id} data-testid={`home-choice-${c.id}`}
                onClick={() => { localStorage.setItem("bsg_intent", c.intent); navigate("/auth?role=passenger"); }}
                className="group relative overflow-hidden rounded-3xl text-left h-56 sm:h-72 ring-1 ring-black/5 shadow-[0_6px_24px_rgba(11,19,32,0.08)] hover:-translate-y-1 hover:shadow-[0_14px_36px_rgba(11,19,32,0.18)] transition-all duration-300">
                <img src={c.img} alt={c.label} className={`absolute inset-0 w-full h-full object-cover ${c.id === "car" ? "object-right" : "object-center"} transition-transform duration-500 group-hover:scale-105`} />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1320]/78 via-[#0B1320]/5 to-transparent" />
                <div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-3xl" />
                <div className="absolute top-3 left-3 w-11 h-11 rounded-2xl bg-white/95 backdrop-blur flex items-center justify-center text-2xl shadow">{c.emoji}</div>
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5">
                  <div className="text-white font-head font-black text-xl sm:text-2xl leading-tight">{c.label}</div>
                  <div className="text-white/80 text-xs sm:text-sm">{c.desc}</div>
                  <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#FF6B00] group-hover:bg-[#E05D00] text-white text-sm font-bold px-4 py-2 transition-colors">
                    Choisir <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </div>
              </button>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[#64748B] text-xs sm:text-sm">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-[#00A651]" /> Chauffeurs vérifiés</span>
            <span className="inline-flex items-center gap-1.5"><Wallet className="w-4 h-4 text-[#00A651]" /> Mobile Money (MTN / Orange)</span>
            <button data-testid="home-become-driver" onClick={() => navigate("/recruter")} className="inline-flex items-center gap-1.5 font-semibold text-[#E05D00] hover:underline"><CarTaxiFront className="w-4 h-4" /> Devenir chauffeur</button>
          </div>
        </div>
      </section>

      {/* HERO — image plein centre-ville, texte superposé */}
      <section data-testid="hero-banner" className="relative overflow-hidden">
        <div className="relative min-h-[220px] md:min-h-[280px]">
          {heroImages.map((src, i) => (
            <img key={src} src={src} alt={zone === "europe" ? "Nold Go — chauffeur et livreur professionnels en tenue Nold Go dans une rue européenne (Bruxelles / Anvers)" : "Mboa Go — moto (bendskin) dans une ville propre au Cameroun"} className="absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-1000" style={{ opacity: heroIdx === i ? 1 : 0 }} />
          ))}
          <div className="absolute bottom-4 left-0 right-0 z-20 flex justify-center gap-2" data-testid="hero-carousel-dots">
            {heroImages.map((_, i) => (
              <button key={i} data-testid={`hero-dot-${i}`} onClick={() => setHeroIdx(i)} aria-label={`Image ${i + 1}`} className={`h-2 rounded-full transition-all ${heroIdx === i ? "w-6 bg-[#F26419]" : "w-2 bg-white/60 hover:bg-white/90"}`} />
            ))}
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/45 via-black/20 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
          <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-8 md:py-10 flex flex-col justify-center min-h-[220px] md:min-h-[280px]">
            <motion.div {...fade(0)} className="max-w-2xl">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/25 text-white text-xs font-semibold uppercase tracking-widest">
                <span className="w-1.5 h-1.5 rounded-full bg-[#F26419]" /> {zone === "europe" ? t("hero.euBadge") : t("hero.badge")}
              </span>
              <h1 className="mt-4 font-head font-black text-white text-xl sm:text-2xl lg:text-3xl tracking-tight leading-[1.1] [text-shadow:0_2px_10px_rgba(0,0,0,0.35)]">
                {t("hero.title", { place: heroPlace })}
              </h1>
              <p className="mt-4 text-white/90 text-sm sm:text-base max-w-xl leading-relaxed">
                {zone === "europe" ? t("hero.euSubtitle") : t("hero.subtitle")}
              </p>
              <div className="mt-6 flex items-center gap-6 text-white/80 text-sm">
                <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#FFCC00]" /> {t("safety.f1t")}</span>
                <span className="flex items-center gap-2"><Wallet className="w-4 h-4 text-[#FFCC00]" /> {zone === "europe" ? "EUR (€)" : (currentCountry?.currency || "FCFA")}</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ACTUS PAR VILLE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-8">
        <ActusVille />
      </section>

      {/* PAYMENTS ACCEPTED */}
      {zone !== "europe" && (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-10">
        <div data-testid="payments-banner" className="rounded-2xl border border-[#E2DFD8] bg-white p-5 sm:p-6 flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#F0EBE1] text-[#75807A] text-[11px] font-bold uppercase tracking-widest shrink-0">{t("payments.badge")}</span>
          <div className="flex-1 text-center sm:text-left">
            <p className="font-head font-bold text-[#1A1A1A] text-base sm:text-lg">{t("payments.title")}</p>
            <div className="mt-3 flex flex-wrap justify-center sm:justify-start gap-2.5">
              <span className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-[#FFF3E8] border border-[#D94F2B]/30 text-[#1A1A1A] text-sm font-semibold"><span className="w-2.5 h-2.5 rounded-full bg-[#D94F2B]" /> {t("payments.om")}</span>
              <span className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-[#FFFBE6] border border-[#FFCC00]/60 text-[#1A1A1A] text-sm font-semibold"><span className="w-2.5 h-2.5 rounded-full bg-[#FFCC00]" /> {t("payments.momo")}</span>
              <span className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-[#F0F7F2] border border-[#16a34a]/30 text-[#1A1A1A] text-sm font-semibold"><span className="w-2.5 h-2.5 rounded-full bg-[#16a34a]" /> {t("payments.cash")}</span>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ABOUT US */}
      <section id="about" className="max-w-7xl mx-auto px-4 sm:px-6 py-20 md:py-28">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-start">
          <motion.div {...fade(0)}>
            <p className="text-[#D94F2B] text-sm font-semibold uppercase tracking-[0.1em]">{t("about.kicker")}</p>
            <h2 className="mt-3 font-head font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1A1A1A]">{t("about.title")}</h2>
            <p className="mt-5 text-[#3B423E] text-lg leading-relaxed">{t("about.p1")}</p>
            <div data-testid="about-objective" className="mt-6 rounded-2xl bg-gradient-to-br from-[#00A651] to-[#00733A] text-white p-6 shadow-[0_8px_24px_rgba(0,166,81,0.25)]">
              <div className="inline-flex items-center gap-2 text-[#FFE08A] font-bold uppercase text-xs tracking-widest"><Target className="w-4 h-4" /> {t("about.objectiveTitle")}</div>
              <p className="mt-3 text-white/85 leading-relaxed">{zone === "europe" ? "Offrir à chacun un moyen de transport fiable et abordable, tout en créant des revenus stables et dignes pour des milliers de chauffeurs — payés simplement en € par carte." : t("about.objective")}</p>
            </div>
          </motion.div>
          <motion.div {...fade(0.1)} className="grid sm:grid-cols-2 gap-5">
            {[
              { icon: ShieldCheck, tt: t("about.v1t"), d: t("about.v1d") },
              { icon: Wallet, tt: t("about.v2t"), d: t("about.v2d") },
              { icon: Users, tt: t("about.v3t"), d: t("about.v3d") },
              { icon: Star, tt: t("about.v4t"), d: t("about.v4d") },
            ].map((it, i) => (
              <div key={i} data-testid={`about-value-${i}`} className="rounded-3xl bg-white border border-[#E2DFD8] p-6 shadow-[0_4px_20px_rgba(0,0,0,0.03)]">
                <span className="w-12 h-12 rounded-2xl bg-[#F0EBE1] flex items-center justify-center"><it.icon className="w-6 h-6 text-[#D94F2B]" /></span>
                <h3 className="mt-4 font-head font-bold text-lg text-[#1A1A1A]">{it.tt}</h3>
                <p className="mt-2 text-[#3B423E] text-sm leading-relaxed">{it.d}</p>
              </div>
            ))}
          </motion.div>
        </div>

        {zone === "europe" && <EuropeAboutCarousel />}

        {false && zone !== "europe" && (
        <motion.div {...fade(0.15)} data-testid="hr-documents" className="mt-12 rounded-3xl border border-[#E2DFD8] bg-white p-6 sm:p-8">
          <div className="flex items-center gap-2 text-[#D94F2B] text-xs font-bold uppercase tracking-[0.1em]">
            <FileText className="w-4 h-4" /> {t("docs.kicker")}
          </div>
          <h3 className="mt-3 font-head font-black text-2xl sm:text-3xl tracking-tight text-[#1A1A1A]">{t("docs.title")}</h3>
          <p className="mt-3 text-[#3B423E] leading-relaxed max-w-2xl">{t("docs.subtitle")}</p>
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            {[
                  { key: "contrat-travail", tt: t("docs.contractTitle"), d: t("docs.contractDesc") },
                  { key: "charte-rh", tt: t("docs.charterTitle"), d: t("docs.charterDesc") },
            ].map((it) => (
              <a
                key={it.key}
                data-testid={`doc-download-${it.key}`}
                href={`${API}/documents/${it.key}.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-start gap-4 rounded-2xl border border-[#E2DFD8] bg-[#FDF8F0] p-5 hover:border-[#D94F2B] hover:bg-white transition-colors"
              >
                <span className="w-12 h-12 rounded-2xl bg-[#1A1A1A] text-white flex items-center justify-center shrink-0"><FileText className="w-6 h-6" /></span>
                <span className="min-w-0">
                  <span className="block font-head font-bold text-[#1A1A1A]">{it.tt}</span>
                  <span className="block mt-1 text-sm text-[#75807A] leading-relaxed">{it.d}</span>
                  <span className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#D94F2B]"><Download className="w-4 h-4" /> {t("docs.download")}</span>
                </span>
              </a>
            ))}
          </div>
        </motion.div>
        )}

      </section>

      {/* TEAM ON THE GROUND */}
      {/* DISCLAIMER */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-10">
        <div data-testid="disclaimer-band" className="rounded-2xl border border-[#E2DFD8] bg-[#F0EBE1] px-5 py-4 flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-[#75807A] mt-0.5 shrink-0" />
          <p className="text-sm text-[#3B423E] leading-relaxed">
            <span className="font-bold text-[#1A1A1A]">Clause de non-responsabilité :</span> Mboa Go est une plateforme de mise en relation. Chaque chauffeur est un partenaire indépendant, <span className="font-semibold">couvert par sa propre assurance automobile</span>. Mboa Go décline toute responsabilité quant aux chauffeurs et aux courses effectuées.
          </p>
        </div>
      </section>

      {/* LAUNCH PROMO */}
      {promo?.free && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 md:pt-20">
          <motion.div {...fade(0)} data-testid="promo-section" className="relative overflow-hidden rounded-[2rem] bg-[#1A1A1A] text-white p-8 sm:p-12">
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#F26419]/25 blur-3xl" />
            <div className="relative">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F26419] text-white text-xs font-bold uppercase tracking-widest">{t("promo.badge")}</span>
              <h2 className="mt-5 font-head font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight">{t("promo.title")}</h2>
              <p className="mt-4 max-w-2xl text-white/75 text-base sm:text-lg leading-relaxed">{t("promo.text")}</p>
              <div className="mt-5 inline-flex items-center gap-2 text-[#F26419] font-semibold"><Clock className="w-4 h-4" /> {t("promo.until")} {promoDate}</div>
              {countdown && (
                <div data-testid="promo-countdown" className="mt-6">
                  <div className="text-sm font-semibold text-white/70">{t("promo.ends")}</div>
                  <div className="mt-3 flex items-center gap-3">
                    {[{ v: countdown.d, l: t("promo.days") }, { v: countdown.h, l: t("promo.hours") }, { v: countdown.m, l: t("promo.mins") }, { v: countdown.s, l: t("promo.secs") }].map((c, i) => (
                      <div key={i} className="flex flex-col items-center">
                        <span className="w-16 h-16 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center font-head font-black text-2xl text-white tabular-nums">{String(c.v).padStart(2, "0")}</span>
                        <span className="mt-1 text-[11px] text-white/60 uppercase tracking-wide">{c.l}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <button data-testid="promo-cta-passenger" onClick={() => navigate("/auth?role=passenger")} className="h-14 px-7 rounded-2xl bg-[#F26419] hover:bg-[#e55f00] text-white font-bold text-base inline-flex items-center justify-center gap-2 transition-colors">
                  <MapPin className="w-5 h-5" /> {t("promo.ctaPassenger")}
                </button>
                <button data-testid="promo-cta-driver" onClick={() => navigate("/auth?role=driver")} className="h-14 px-7 rounded-2xl bg-[#F26419] hover:bg-[#D85312] text-[#1A1A1A] font-bold text-base inline-flex items-center justify-center gap-2 transition-colors">
                  <CarTaxiFront className="w-5 h-5" /> {t("promo.ctaDriver")}
                </button>
              </div>
            </div>
          </motion.div>
        </section>
      )}

      {/* VEHICLE OPTIONS */}
      <section id="vehicles" className="max-w-7xl mx-auto px-4 sm:px-6 py-20 md:py-28">
        <motion.div {...fade(0)} className="max-w-2xl">
          <p className="text-[#D94F2B] text-sm font-semibold uppercase tracking-[0.1em]">{t("delivery.vehKicker")}</p>
          <h2 className="mt-3 font-head font-bold text-3xl sm:text-4xl tracking-tight text-[#1A1A1A]">{t("delivery.vehTitle")}</h2>
          <p className="mt-4 text-[#3B423E] text-lg">{zone === "europe" ? "Voiture ou vélo — au meilleur prix, en €." : t("delivery.vehSubtitle")}</p>
        </motion.div>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {(zone === "europe" ? [
            { img: CAR_IMG, icon: Car, title: t("delivery.modeCar"), desc: t("delivery.vehCarDesc"), price: "Dès 4 €", eta: t("delivery.vehCarEta") },
            { img: VELO_IMG, icon: Bike, title: t("delivery.modeVelo"), desc: t("delivery.vehVeloDesc"), price: "Dès 2 €", eta: t("delivery.vehVeloEta") },
            { img: DELIVERY_IMG, icon: Package, title: t("delivery.modeParcel"), desc: t("delivery.vehDeliveryDesc"), price: "+1 € manutention", eta: t("delivery.vehDeliveryEta") },
          ] : [
            { img: MOTO_IMG, icon: Bike, title: t("delivery.modeMoto"), desc: t("delivery.vehMotoDesc"), price: t("delivery.vehMotoPrice"), eta: t("delivery.vehMotoEta") },
            { img: CAR_IMG, icon: Car, title: t("delivery.modeCar"), desc: t("delivery.vehCarDesc"), price: t("delivery.vehCarPrice"), eta: t("delivery.vehCarEta") },
            { img: VELO_IMG, icon: Bike, title: t("delivery.modeVelo"), desc: t("delivery.vehVeloDesc"), price: t("delivery.vehVeloPrice"), eta: t("delivery.vehVeloEta") },
            { img: DELIVERY_IMG, icon: Package, title: t("delivery.modeParcel"), desc: t("delivery.vehDeliveryDesc"), price: t("delivery.vehDeliveryPrice"), eta: t("delivery.vehDeliveryEta") },
          ]).map((v, i) => (
            <motion.div key={i} {...fade(i * 0.1)} data-testid={`vehicle-card-${i}`} className="group rounded-3xl overflow-hidden bg-white border border-[#E2DFD8] shadow-[0_4px_20px_rgba(0,0,0,0.03)]">
              <div className="h-48 overflow-hidden">
                <img src={v.img} alt={v.title} className={`w-full h-full object-cover ${v.img === CAR_IMG ? "object-right" : "object-center"} transition-transform duration-500 group-hover:scale-105`} />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3">
                  <span className="w-11 h-11 rounded-xl bg-[#F0EBE1] flex items-center justify-center"><v.icon className="w-5 h-5 text-[#D94F2B]" /></span>
                  <h3 className="font-head font-bold text-xl text-[#1A1A1A]">{v.title}</h3>
                </div>
                <p className="mt-3 text-[#3B423E] leading-relaxed">{v.desc}</p>
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-[#D94F2B] font-semibold text-sm">{v.price}</span>
                  <span data-testid={`vehicle-eta-${i}`} className="text-[#75807A] text-xs flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {v.eta}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CONFIANCE & VILLES — bento lumineux */}
      <section data-testid="trust-showcase" className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24">
        <motion.div {...fade(0)} className="max-w-2xl">
          <p className="text-[#00A651] text-sm font-semibold uppercase tracking-[0.1em]">La confiance, partout</p>
          <h2 className="mt-3 font-head font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1A1A1A]">
            Toute la ville, <span className="text-[#F26419]">à portée de main</span>
          </h2>
          <p className="mt-4 text-[#3B423E] text-lg leading-relaxed">Des chauffeurs vérifiés, des prix clairs et un paiement simple — pour des milliers de trajets sûrs chaque jour.</p>
        </motion.div>

        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 auto-rows-[150px] sm:auto-rows-[170px]">
          {/* Grande tuile ville */}
          <motion.div {...fade(0)} data-testid="trust-tile-city" className="group relative col-span-2 row-span-2 rounded-3xl overflow-hidden ring-1 ring-black/5 shadow-[0_8px_30px_rgba(11,19,32,0.10)]">
            <img src={CITY_IMG} alt="Ville au Cameroun vue du ciel" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B1320]/80 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
              <div className="inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur px-3 py-1 text-white/90 text-xs font-semibold"><MapPin className="w-3.5 h-3.5" /> Yaoundé · Douala · Bafoussam</div>
              <div className="mt-3 text-white font-head font-black text-2xl sm:text-3xl leading-tight">Une plateforme née au Cameroun</div>
            </div>
          </motion.div>

          {/* Carte stat verte */}
          <motion.div {...fade(0.05)} data-testid="trust-tile-rating" className="rounded-3xl p-5 bg-gradient-to-br from-[#00A651] to-[#00733A] text-white flex flex-col justify-between shadow-[0_8px_24px_rgba(0,166,81,0.25)]">
            <Star className="w-6 h-6 text-[#FFE08A] fill-[#FFE08A]" />
            <div>
              <div className="font-head font-black text-4xl leading-none">4.9<span className="text-lg text-white/70">/5</span></div>
              <div className="mt-1 text-white/85 text-sm">Note moyenne chauffeurs</div>
            </div>
          </motion.div>

          {/* Chauffeurs vérifiés */}
          <motion.div {...fade(0.1)} data-testid="trust-tile-drivers" className="group relative rounded-3xl overflow-hidden ring-1 ring-black/5 shadow-[0_8px_24px_rgba(11,19,32,0.08)]">
            <img src={DRIVERS_IMG} alt="Chauffeurs moto souriants" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B1320]/75 to-transparent" />
            <div className="absolute bottom-3 left-4 right-4 flex items-center gap-2 text-white text-sm font-semibold"><ShieldCheck className="w-4 h-4 text-[#FFCC00]" /> Chauffeurs vérifiés</div>
          </motion.div>

          {/* Paiement */}
          <motion.div {...fade(0.15)} data-testid="trust-tile-pay" className="rounded-3xl p-5 bg-[#FFF3E8] border border-[#F3DFC9] flex flex-col justify-between">
            <span className="w-11 h-11 rounded-2xl bg-white flex items-center justify-center shadow-sm"><Wallet className="w-5 h-5 text-[#F26419]" /></span>
            <div>
              <div className="font-head font-bold text-[#1A1A1A] text-lg leading-tight">Paiement simple</div>
              <div className="mt-1 text-[#75807A] text-sm">MTN · Orange Money · Espèces</div>
            </div>
          </motion.div>

          {/* Cliente en ville */}
          <motion.div {...fade(0.2)} data-testid="trust-tile-client" className="group relative rounded-3xl overflow-hidden ring-1 ring-black/5 shadow-[0_8px_24px_rgba(11,19,32,0.08)]">
            <img src={RIDER_WOMAN} alt="Cliente commandant une course en ville" className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B1320]/75 to-transparent" />
            <div className="absolute bottom-3 left-4 right-4 flex items-center gap-2 text-white text-sm font-semibold"><Clock className="w-4 h-4 text-[#FFCC00]" /> En quelques secondes</div>
          </motion.div>

          {/* CTA */}
          <motion.div {...fade(0.25)} data-testid="trust-tile-cta" className="rounded-3xl p-5 bg-[#1A1A1A] text-white flex flex-col justify-between">
            <Route className="w-6 h-6 text-[#F26419]" />
            <div>
              <div className="font-head font-bold text-lg leading-tight">Prêt à partir ?</div>
              <button data-testid="trust-cta-btn" onClick={() => navigate("/auth?role=passenger")} className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#F26419] hover:bg-[#D85312] text-white text-sm font-bold px-4 py-2 transition-colors">Commander <ArrowRight className="w-4 h-4" /></button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="max-w-7xl mx-auto px-4 sm:px-6 py-20 md:py-32">
        <motion.div {...fade(0)} className="max-w-2xl">
          <p className="text-[#D94F2B] text-sm font-semibold uppercase tracking-[0.1em]">01 — {t("how.title")}</p>
          <h2 className="mt-3 font-head font-bold text-3xl sm:text-4xl tracking-tight text-[#1A1A1A]">{t("how.title")}</h2>
          <p className="mt-4 text-[#3B423E] text-lg">{t("how.subtitle")}</p>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-6">
          {[
            { icon: Route, t: t("how.s1t"), d: t("how.s1d"), span: "md:col-span-4", bg: "bg-white" },
            { icon: CreditCard, t: zone === "europe" ? "Prix transparents en €" : t("how.s2t"), d: t("how.s2d"), span: "md:col-span-4", bg: "bg-white" },
            { icon: Bike, t: t("how.s3t"), d: t("how.s3d"), span: "md:col-span-4", bg: "bg-white" },
          ].map((it, i) => (
            <motion.div key={i} {...fade(i * 0.1)} className={`${it.span} ${it.bg} rounded-3xl border border-[#E2DFD8] p-8 shadow-[0_4px_20px_rgba(0,0,0,0.03)]`}>
              <div className="w-14 h-14 rounded-2xl bg-[#F0EBE1] flex items-center justify-center">
                <it.icon className="w-7 h-7 text-[#D94F2B]" strokeWidth={2} />
              </div>
              <div className="mt-6 text-5xl font-head font-black text-[#F0EBE1]">0{i + 1}</div>
              <h3 className="mt-2 font-head font-bold text-xl text-[#1A1A1A]">{it.t}</h3>
              <p className="mt-2 text-[#3B423E] leading-relaxed">{it.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* SAFETY */}
      <section id="safety" className="hidden bg-[#1A1A1A] py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div {...fade(0)}>
            <p className="text-[#D94F2B] text-sm font-semibold uppercase tracking-[0.1em]">02 — {t("safety.title")}</p>
            <h2 className="mt-3 font-head font-bold text-3xl sm:text-4xl tracking-tight text-white">{t("safety.title")}</h2>
            <p className="mt-4 text-white/70 text-lg">{t("safety.subtitle")}</p>
            <div className="mt-10 space-y-6">
              {[
                { icon: ShieldCheck, t: t("safety.f1t"), d: t("safety.f1d") },
                { icon: CreditCard, t: t("safety.f2t"), d: t("safety.f2d") },
                { icon: Star, t: t("safety.f3t"), d: t("safety.f3d") },
              ].map((it, i) => (
                <div key={i} className="flex gap-4">
                  <div className="w-12 h-12 shrink-0 rounded-xl bg-[#F26419]/20 flex items-center justify-center">
                    <it.icon className="w-6 h-6 text-[#D94F2B]" />
                  </div>
                  <div>
                    <h3 className="font-head font-semibold text-lg text-white">{it.t}</h3>
                    <p className="text-white/60">{it.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div {...fade(0.15)} className="relative">
            <img src={TRAFFIC} alt="Circulation moto Afrique" className="rounded-3xl w-full h-[420px] object-cover" />
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 shadow-2xl hidden sm:block">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-[#F26419] flex items-center justify-center"><Star className="w-6 h-6 text-white fill-white" /></div>
                <div>
                  <div className="font-head font-bold text-2xl text-[#1A1A1A]">4.9<span className="text-base text-[#75807A]">/5</span></div>
                  <div className="text-xs text-[#75807A]">Note moyenne chauffeurs</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* COVERAGE */}
      {zone !== "europe" && (
      <section id="coverage" className="hidden max-w-7xl mx-auto px-4 sm:px-6 py-20 md:py-32">
        <motion.div {...fade(0)} className="max-w-2xl">
          <p className="text-[#D94F2B] text-sm font-semibold uppercase tracking-[0.1em]">03 — {t("coverage.title")}</p>
          <h2 className="mt-3 font-head font-bold text-3xl sm:text-4xl tracking-tight text-[#1A1A1A]">{t("coverage.title")}</h2>
          <p className="mt-4 text-[#3B423E] text-lg">{t("coverage.subtitle")}</p>
        </motion.div>

        <motion.div {...fade(0.1)} className="mt-8 rounded-3xl overflow-hidden border border-[#E2DFD8] shadow-xl">
          <img src="/og-image.jpg" alt="Mboa Go — Yaoundé, Douala, Bafoussam & Grand Nord" data-testid="coverage-affiche" className="w-full object-cover" />
        </motion.div>

        <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {countries.map((c, i) => (
            <motion.div
              key={c.code}
              {...fade(i * 0.05)}
              data-testid={`coverage-${c.code}`}
              className={`rounded-2xl border p-5 ${c.active ? "bg-[#F26419] border-[#D94F2B] text-white" : "bg-white border-[#E2DFD8]"}`}
            >
              <div className="text-3xl">{c.flag}</div>
              <div className={`mt-3 font-head font-semibold ${c.active ? "text-white" : "text-[#1A1A1A]"}`}>{c.name}</div>
              <div className={`mt-3 inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${c.active ? "bg-white/20 text-white" : "bg-[#F0EBE1] text-[#75807A]"}`}>
                {c.active && <Check className="w-3 h-3" />} {c.active ? t("coverage.live") : t("coverage.soon")}
              </div>
              <div className={`mt-3 text-xs ${c.active ? "text-white/80" : "text-[#75807A]"}`}>
                {fcfa(c.base_fare)} + {fcfa(c.per_km)}/km
              </div>
            </motion.div>
          ))}
        </div>
      </section>
      )}

      {/* INSTALL APP */}
      <InstallApp />

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-24">
        <motion.div {...fade(0)} className="rounded-[2rem] bg-[#F26419] p-10 md:p-16 relative overflow-hidden">
          <div className="absolute -right-10 -top-10 w-64 h-64 rounded-full bg-white/10" />
          <div className="relative max-w-xl">
            <h2 className="font-head font-black text-3xl sm:text-4xl text-white tracking-tight">{t("cta.title")}</h2>
            <p className="mt-4 text-white/90 text-lg">{t("cta.subtitle")}</p>
            <button
              data-testid="cta-bottom"
              onClick={() => navigate("/auth?role=passenger")}
              className="mt-8 h-14 px-8 rounded-2xl bg-white text-[#D94F2B] font-bold text-lg inline-flex items-center gap-2 hover:-translate-y-1 transition-transform"
            >
              {t("cta.start")} <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </motion.div>
      </section>

      {/* SUBSCRIPTION */}
      {false && zone !== "europe" && (
      <section id="subscription" className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-[#D94F2B] text-sm font-semibold uppercase tracking-[0.1em]">{t("subscription.kicker")}</p>
          <h2 className="mt-3 font-head font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1A1A1A]">{t("subscription.title")}</h2>
          <p className="mt-4 text-[#3B423E] text-lg">{t("subscription.subtitle")}</p>
        </div>
        <div data-testid="subscription-card" className="mt-10 max-w-md mx-auto rounded-[2rem] bg-[#1A1A1A] text-white p-8 text-center">
          <span className="inline-block text-xs font-bold uppercase tracking-widest text-[#F26419]">{t("subscription.badge")}</span>
          <div className="mt-4 flex items-end justify-center gap-1">
            <span className="font-head font-black text-6xl">2 950</span>
            <span className="mb-2 text-white/70 font-semibold">FCFA{t("subscription.perMonth")}</span>
          </div>
          <p className="mt-1 text-white/60 text-sm">{t("subscription.maintenance")}</p>
          <p className="mt-1 text-white/50 text-xs">{t("subscription.veloNote")}</p>
          <div className="mt-6 space-y-2 text-left inline-block">
            <div className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-[#F26419]" /> {t("subscription.b1")}</div>
            <div className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-[#F26419]" /> {t("subscription.b2")}</div>
            <div className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-[#F26419]" /> {t("subscription.b3")}</div>
          </div>
          <div className="mt-6 rounded-xl bg-[#16a34a]/20 border border-[#16a34a]/40 p-3 text-sm text-[#8ef0b0] font-semibold">{t("subscription.freeNow")}</div>
        </div>
      </section>
      )}

      {/* BECOME AGENT */}
      <section id="become-agent" className="hidden max-w-7xl mx-auto px-4 sm:px-6 pb-8">
        <div className="rounded-[2rem] bg-[#1A1A1A] text-white p-8 sm:p-12 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-[#F26419] text-sm font-semibold uppercase tracking-[0.1em]">{t("becomeAgent.kicker")}</p>
            <h2 className="mt-3 font-head font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight">{t("becomeAgent.title")}</h2>
            <p className="mt-4 text-white/80 text-lg leading-relaxed">{t("becomeAgent.subtitle")}</p>
            <div className="mt-6 space-y-2 text-white/90 text-sm">
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-[#F26419]" /> {t("becomeAgent.b1")}</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-[#F26419]" /> {t("becomeAgent.b2")}</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-[#F26419]" /> {t("becomeAgent.b3")}</div>
            </div>
          </div>
          <div>
            {agentAppSent ? (
              <div data-testid="agent-app-success" className="rounded-2xl bg-white/10 border border-[#F26419]/40 p-8 text-center">
                <div className="w-14 h-14 mx-auto rounded-full bg-[#F26419] flex items-center justify-center"><Check className="w-7 h-7 text-[#1A1A1A]" /></div>
                <h3 className="mt-4 font-head font-bold text-xl">{t("becomeAgent.successTitle")}</h3>
                <p className="mt-2 text-white/80">{t("becomeAgent.successMsg")}</p>
                <button data-testid="agent-app-reset" onClick={() => setAgentAppSent(false)} className="mt-5 h-11 px-5 rounded-xl bg-white text-[#1A1A1A] font-semibold">{t("becomeAgent.another")}</button>
              </div>
            ) : (
              <form onSubmit={submitAgentApp} className="space-y-3">
                <input data-testid="agent-app-name" value={agentApp.name} onChange={(e) => setAgentApp({ ...agentApp, name: e.target.value })} placeholder={t("becomeAgent.namePh")} required className="w-full h-12 rounded-xl bg-white/10 border-2 border-white/20 px-4 text-white placeholder:text-white/50 focus:outline-none focus:border-[#F26419]" />
                <input data-testid="agent-app-email" type="email" value={agentApp.email} onChange={(e) => setAgentApp({ ...agentApp, email: e.target.value })} placeholder="Votre email (pour vous connecter)" required className="w-full h-12 rounded-xl bg-white/10 border-2 border-white/20 px-4 text-white placeholder:text-white/50 focus:outline-none focus:border-[#F26419]" />
                <div className="grid sm:grid-cols-2 gap-3">
                  <input data-testid="agent-app-phone" value={agentApp.phone} onChange={(e) => setAgentApp({ ...agentApp, phone: e.target.value })} placeholder={t("becomeAgent.phonePh")} className="h-12 rounded-xl bg-white/10 border-2 border-white/20 px-4 text-white placeholder:text-white/50 focus:outline-none focus:border-[#F26419]" />
                  <input data-testid="agent-app-zone" value={agentApp.zone} onChange={(e) => setAgentApp({ ...agentApp, zone: e.target.value })} placeholder={t("becomeAgent.zonePh")} className="h-12 rounded-xl bg-white/10 border-2 border-white/20 px-4 text-white placeholder:text-white/50 focus:outline-none focus:border-[#F26419]" />
                </div>
                <textarea data-testid="agent-app-message" value={agentApp.message} onChange={(e) => setAgentApp({ ...agentApp, message: e.target.value })} placeholder={t("becomeAgent.messagePh")} rows={3} className="w-full rounded-xl bg-white/10 border-2 border-white/20 px-4 py-3 text-white placeholder:text-white/50 focus:outline-none focus:border-[#F26419]" />
                <button data-testid="agent-app-submit" type="submit" className="w-full h-14 rounded-xl bg-[#F26419] hover:bg-[#D85312] text-[#1A1A1A] font-bold transition-colors">{t("becomeAgent.submit")}</button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* COMPLAINTS */}
      <section id="complaints" className="hidden max-w-7xl mx-auto px-4 sm:px-6 pb-24">
        <div className="rounded-[2rem] bg-white border border-[#E2DFD8] p-8 sm:p-12 grid lg:grid-cols-2 gap-10">
          <div>
            <p className="text-[#D94F2B] text-sm font-semibold uppercase tracking-[0.1em]">{t("complaint.kicker")}</p>
            <h2 className="mt-3 font-head font-black text-3xl sm:text-4xl tracking-tight text-[#1A1A1A]">{t("complaint.title")}</h2>
            <p className="mt-4 text-[#3B423E] text-lg leading-relaxed">{t("complaint.subtitle")}</p>
            <div className="mt-6 space-y-2 text-[#3B423E] text-sm">
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-[#D94F2B]" /> {t("complaint.b1")}</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-[#D94F2B]" /> {t("complaint.b2")}</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-[#D94F2B]" /> {t("complaint.b3")}</div>
            </div>
          </div>
          <div>
            {complaintSent ? (
              <div data-testid="complaint-success" className="rounded-2xl bg-[#16a34a]/10 border border-[#16a34a]/30 p-8 text-center">
                <div className="w-14 h-14 mx-auto rounded-full bg-[#16a34a] flex items-center justify-center"><Check className="w-7 h-7 text-white" /></div>
                <h3 className="mt-4 font-head font-bold text-xl text-[#1A1A1A]">{t("complaint.successTitle")}</h3>
                <p className="mt-2 text-[#3B423E]">{t("complaint.successMsg")}</p>
                <button data-testid="complaint-reset" onClick={() => setComplaintSent(false)} className="mt-5 h-11 px-5 rounded-xl bg-[#1A1A1A] text-white font-semibold">{t("complaint.another")}</button>
              </div>
            ) : (
              <form onSubmit={submitComplaint} className="space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  <input data-testid="complaint-name" value={complaint.name} onChange={(e) => setComplaint({ ...complaint, name: e.target.value })} placeholder={t("complaint.namePh")} className="h-12 rounded-xl border-2 border-[#E2DFD8] px-4 focus:outline-none focus:border-[#D94F2B]" />
                  <input data-testid="complaint-phone" value={complaint.phone} onChange={(e) => setComplaint({ ...complaint, phone: e.target.value })} placeholder={t("complaint.phonePh")} className="h-12 rounded-xl border-2 border-[#E2DFD8] px-4 focus:outline-none focus:border-[#D94F2B]" />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <select data-testid="complaint-category" value={complaint.category} onChange={(e) => setComplaint({ ...complaint, category: e.target.value })} className="h-12 rounded-xl border-2 border-[#E2DFD8] px-4 bg-white focus:outline-none focus:border-[#D94F2B]">
                    <option value="litige">{t("complaint.catDispute")}</option>
                    <option value="chauffeur">{t("complaint.catDriver")}</option>
                    <option value="paiement">{t("complaint.catPayment")}</option>
                    <option value="securite">{t("complaint.catSafety")}</option>
                    <option value="autre">{t("complaint.catOther")}</option>
                  </select>
                  <input data-testid="complaint-ride" value={complaint.ride_ref} onChange={(e) => setComplaint({ ...complaint, ride_ref: e.target.value })} placeholder={t("complaint.ridePh")} className="h-12 rounded-xl border-2 border-[#E2DFD8] px-4 focus:outline-none focus:border-[#D94F2B]" />
                </div>
                <textarea data-testid="complaint-message" required value={complaint.message} onChange={(e) => setComplaint({ ...complaint, message: e.target.value })} placeholder={t("complaint.messagePh")} rows={5} className="w-full rounded-xl border-2 border-[#E2DFD8] px-4 py-3 focus:outline-none focus:border-[#D94F2B]" />
                <button data-testid="complaint-submit" type="submit" className="w-full h-14 rounded-xl bg-[#F26419] hover:bg-[#D85312] text-white font-bold transition-colors">{t("complaint.submit")}</button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#E2DFD8] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#F26419] to-[#D94F2B] flex items-center justify-center"><brand.Icon className="w-4 h-4 text-white" /></span>
            <span className="font-head font-extrabold text-lg">{brand.firstWord}<span className="text-[#F26419]"> Go</span></span>
          </div>
          <p className="text-[#75807A] text-sm text-center">{t("footer.tagline")}</p>
          <p data-testid="footer-nold-note" className="text-[#9AA39D] text-xs text-center max-w-xl">
            Mboa Go opère en Europe sous la marque <span className="font-semibold text-[#75807A]">Nold Go</span> — courses en voiture et livraisons à vélo, paiement par carte (€). Une même entreprise, deux marchés : l'Afrique Centrale (Mboa Go) et l'Europe (Nold Go).
          </p>
          <div className="flex items-center gap-4">
            {zone !== "europe" && (<>
            <button
              data-testid="footer-recruit-link"
              onClick={() => navigate("/recruter")}
              className="text-[#D94F2B] hover:text-[#D85312] text-sm font-semibold transition-colors"
            >
              {t("recruit.title")}
            </button>
            <button
              data-testid="footer-team-link"
              onClick={() => navigate("/equipe")}
              className="text-[#75807A] hover:text-[#D94F2B] text-xs font-medium transition-colors"
            >
              {t("agent.space")}
            </button>
            <button
              data-testid="footer-become-agent-link"
              onClick={() => document.getElementById("become-agent")?.scrollIntoView({ behavior: "smooth" })}
              className="text-[#75807A] hover:text-[#D94F2B] text-xs font-medium transition-colors"
            >
              {t("becomeAgent.cta")}
            </button>
            </>)}
            <button
              data-testid="footer-complaints-link"
              onClick={() => document.getElementById("complaints")?.scrollIntoView({ behavior: "smooth" })}
              className="text-[#75807A] hover:text-[#D94F2B] text-xs font-medium transition-colors"
            >
              {t("complaint.link")}
            </button>
            {isAdmin && (
            <button
              data-testid="footer-admin-link"
              onClick={() => navigate("/auth?role=admin")}
              className="text-[#B8B2A7] hover:text-[#75807A] text-xs transition-colors"
            >
              {t("footer.admin")}
            </button>
            )}
            <button
              data-testid="footer-copyright-admin"
              onClick={() => navigate("/admin")}
              title="Accès administration"
              className="text-[#75807A] text-xs hover:text-[#75807A] cursor-pointer bg-transparent border-0 p-0"
            >
              © 2026 {brand.name}. {t("footer.rights")}
            </button>
          </div>
        </div>
        <div data-testid="footer-legal" className="border-t border-[#E2DFD8]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 text-center text-[11px] leading-relaxed text-[#9AA39D]">
            <span className="font-semibold text-[#75807A]">NOLD Go</span> — Rue Jourdan 71 A0, 1060 Quartier Louise, Bruxelles · Numéro d'entreprise <span className="text-[#75807A]">1040.580.267</span> · TVA <span className="text-[#75807A]">BE 1040.580.267</span>
          </div>
        </div>
      </footer>
      </div>
    </div>
  );
}
